import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';

import { Album } from '@vimeo/gctl-api/models/components/schemas/album/Album';

import { createStore } from './createStore';

interface AlbumState {
  albumId: number;
  albumOwnerId: number;
  hashedPass: string;
  albumSignature: string | null;
  isShowcaseEmbedded: boolean;
  isOffsite: boolean;
  continuousPlay: boolean;
  bannerHeight?: string;
  setContinuousPlay: (checked: boolean) => void;
  clipPlaylistPage: number | null;
  setClipPlaylistPage: (page: number) => void;
  noOfVideos: number | null;
  setNoOfVideos: (noOfVideos: number) => void;
  albumPrivacy: Album['privacy']['view'] | null;
  seoAllowIndexed: boolean | null;
  setSeoAllowIndex: (seoAllowIndexed: boolean) => void;
  setAlbumPrivacy: (privacy: Album['privacy']['view']) => void;
  setAlbumOwnerId: (albumOwnerId: number) => void;
  setBannerHeight: (bannerHeight: string) => void;
  referrer: string;
  showcaseHash: string;
}

const getReferrerValue = () => {
  try {
    if (document?.referrer) {
      return document.referrer;
    }

    return document?.querySelector('meta[name="referrer"]')?.getAttribute('content') || '';
  } catch (error) {
    return '';
  }
};

const createAlbumStore = (initialProps?: Partial<AlbumState>) => {
  return createStore<AlbumState>((set) => ({
    albumId: 0,
    albumOwnerId: 0,
    hashedPass: '',
    albumSignature: null,
    isShowcaseEmbedded: false,
    isOffsite: false,
    continuousPlay: true,
    referrer: '',
    albumPrivacy: null,
    noOfVideos: null,
    seoAllowIndexed: null,
    clipPlaylistPage: null,
    showcaseHash: '',
    setContinuousPlay: (checked: boolean) =>
      set((state) => {
        state.continuousPlay = checked;
      }),
    setClipPlaylistPage: (page: number) =>
      set((state) => {
        state.clipPlaylistPage = page;
      }),
    setNoOfVideos: (noOfVideos: number) =>
      set((state) => {
        state.noOfVideos = noOfVideos;
      }),
    setAlbumPrivacy: (privacy: Album['privacy']['view']) =>
      set((state) => {
        state.albumPrivacy = privacy;
      }),
    setSeoAllowIndex: (seoAllowIndexed: boolean) =>
      set((state) => {
        state.seoAllowIndexed = seoAllowIndexed;
      }),
    setAlbumOwnerId: (albumOwnerId: number) =>
      set((state) => {
        state.albumOwnerId = albumOwnerId;
      }),
    setBannerHeight: (bannerHeight?: string) => {
      set((state) => {
        state.bannerHeight = bannerHeight;
      });
    },
    ...initialProps,
  }));
};

type SEOStore = ReturnType<typeof createAlbumStore>;

const AlbumStoreContext = createContext<SEOStore | null>(null);

interface AlbumStoreProviderProps {
  /** The id for the album */
  albumId: number;
  /** Whether this Provider and its children content are used for iframe embed */
  isShowcaseEmbedded: boolean;
  /** The page signature that's used to verify resource request is from onsite; `null` means it's not ready, empty string means it's ready but no signature can be retrieved */
  albumSignature?: string | null;
  /** used to unlock password protected-showcase GET ALBUM VIDEOS */
  hashedPass?: string;
  /** Whether the showcase is using a custom domain */
  isOffsite?: boolean;
  /** The referrer of the current page */
  referrer?: string;
  /** The showcase unlisted hash */
  showcaseHash?: string;
}

export const AlbumStoreProvider = ({
  children,
  albumId,
  albumSignature,
  hashedPass = '',
  isShowcaseEmbedded,
  isOffsite = false,
  showcaseHash,
}: PropsWithChildren<AlbumStoreProviderProps>) => {
  const [referrer, setReferrer] = useState<string>('');
  const store = useMemo(() => {
    return createAlbumStore({
      albumId,
      albumSignature,
      hashedPass,
      isShowcaseEmbedded,
      isOffsite,
      referrer,
      showcaseHash,
    });
  }, [albumId, albumSignature, hashedPass, isOffsite, isShowcaseEmbedded, referrer, showcaseHash]);

  useEffect(() => {
    setReferrer(getReferrerValue());
  }, []);

  return <AlbumStoreContext.Provider value={store}>{children}</AlbumStoreContext.Provider>;
};

export function useAlbumStore<T>(selector: (state: AlbumState) => T): T {
  const albumStore = useContext(AlbumStoreContext);

  if (!albumStore) {
    throw new Error('useAlbumStore must be used within an AlbumStoreProvider');
  }

  return useStore(albumStore, selector);
}
